"use client";

import type { ReactNode } from "react";
import { preload } from "react-dom";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import type {
  PayloadLegalLinksConfig,
  PopulatedPayloadFormFunnel,
} from "@portal/app/_server/services/formFunnel/payloadTypes";
import { breakpointsOptions } from "@portal/app/_components/theme/options/breakpoints";
import { FormFunnelHeader } from "./FormFunnelHeader";
import { FormFunnelBannerImage } from "./FormFunnelBannerImage";
import { FormFunnelFooter } from "./FormFunnelFooter";
import { FormFunnelLegalLinks } from "./FormFunnelLegalLinks";
import { FormFunnelLandingPageLayout } from "./FormFunnelLandingPageLayout";

export interface FormFunnelPageLayoutProps {
  funnel: Pick<PopulatedPayloadFormFunnel, "desktopLayout" | "heroImage" | "footerConfig"> &
    Partial<Pick<PopulatedPayloadFormFunnel, "title" | "headerConfig" | "landingPageConfig">>;
  legalLinks: PayloadLegalLinksConfig | null;
  progressPercent: number;
  bannerOffset?: number;
  showHeader?: boolean;
  showCenteredFooter?: boolean;
  showFirstStepBanner?: boolean;
  logoClickable?: boolean;
  children: ReactNode;
}

/** Customer page composition without journey state, tracking or persistence. */
export function FormFunnelPageLayout({
  funnel,
  legalLinks,
  progressPercent,
  bannerOffset = 0,
  showHeader = true,
  showCenteredFooter = true,
  showFirstStepBanner = false,
  logoClickable = true,
  children,
}: FormFunnelPageLayoutProps) {
  if (funnel.desktopLayout === "landing") {
    return (
      <FormFunnelLandingPageLayout
        funnel={{ ...funnel, title: funnel.title ?? "" }}
        legalLinks={legalLinks}
        progressPercent={progressPercent}
        bannerOffset={bannerOffset}
        showHeader={showHeader}
        logoClickable={logoClickable}
      >
        {children}
      </FormFunnelLandingPageLayout>
    );
  }
  const pageContentConfig = funnel.footerConfig;
  const isSplitLayout = funnel.desktopLayout === "split";
  const [contentWidth, formWidth] = (pageContentConfig?.columnRatio ?? "60-40")
    .split("-")
    .map(Number);
  const contentOnLeft = pageContentConfig?.contentSide !== "right";
  const heroUrl =
    isSplitLayout && typeof funnel.heroImage === "object" ? funnel.heroImage?.url : undefined;
  // Beside the form (md+) the hero is above the fold; below md it remains lazy.
  if (heroUrl) {
    preload(heroUrl, { as: "image", media: `(min-width: ${breakpointsOptions.values.md}px)` });
  }

  return (
    <Box
      data-testid="form-funnel-page"
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        pb: 4,
        pt: `${bannerOffset}px`,
      }}
    >
      {showHeader && (
        <FormFunnelHeader
          progressPercent={progressPercent}
          isComplete={false}
          topOffset={bannerOffset}
          logoClickable={logoClickable}
        />
      )}
      <Box
        data-testid="form-funnel-body"
        sx={{
          ...(isSplitLayout && {
            display: { xs: "block", md: "grid" },
            maxWidth: { md: "lg" },
            mx: "auto",
            gridTemplateAreas: contentOnLeft
              ? '"banner banner" "sidebar form"'
              : '"banner banner" "form sidebar"',
            gridTemplateColumns: contentOnLeft
              ? `minmax(0, ${contentWidth}fr) minmax(0, ${formWidth}fr)`
              : `minmax(0, ${formWidth}fr) minmax(0, ${contentWidth}fr)`,
            alignItems: "start",
            alignContent: "start",
            columnGap: { md: 6, lg: 8 },
          }),
        }}
      >
        {isSplitLayout && showFirstStepBanner && (
          <Container
            maxWidth="sm"
            sx={{ gridArea: "banner", maxWidth: { md: "none" }, px: { md: 4, lg: 8 }, pt: 4 }}
          >
            <FormFunnelBannerImage image={pageContentConfig?.bannerImage} />
          </Container>
        )}
        <Container
          maxWidth="sm"
          sx={
            isSplitLayout
              ? (theme) => ({
                  gridArea: "form",
                  width: "100%",
                  minWidth: 0,
                  [theme.breakpoints.up("md")]: { maxWidth: theme.breakpoints.values.sm },
                })
              : undefined
          }
        >
          {children}
        </Container>
        {isSplitLayout && (
          <Box
            data-testid="form-funnel-split-sidebar"
            sx={{
              gridArea: "sidebar",
              minWidth: 0,
              width: "100%",
              mx: "auto",
              maxWidth: { xs: "sm", md: "none" },
              px: { xs: 2, sm: 3, md: 0 },
              pt: 4,
              ...(contentOnLeft ? { pl: { md: 4, lg: 8 } } : { pr: { md: 4, lg: 8 } }),
            }}
          >
            {typeof funnel.heroImage === "object" && funnel.heroImage?.url && (
              <Box data-testid="form-funnel-hero" sx={{ mb: 3 }}>
                <FormFunnelBannerImage image={funnel.heroImage} loading="lazy" />
              </Box>
            )}
            {funnel.footerConfig && (
              <FormFunnelFooter
                footerConfig={funnel.footerConfig}
                variant="column"
                desktopSidebar
              />
            )}
          </Box>
        )}
        {!isSplitLayout && showCenteredFooter && funnel.footerConfig && (
          <FormFunnelFooter footerConfig={funnel.footerConfig} />
        )}
      </Box>
      {legalLinks && <FormFunnelLegalLinks config={legalLinks} />}
    </Box>
  );
}
